import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
/**
 * Retries a request once, with a fresh CSRF token, when the server turned
 * it down for a stale one (400 csrf_token_mismatch) - instead of showing
 * core's generic "something went wrong" and failing the same way on every
 * retry until the page is reloaded.
 *
 * The token comes with the page and belongs to its session. A page left open
 * past the session's lifetime (120 min) - a tablet or phone tab, typically -
 * then posts a token the new session doesn't know. Core only ever learns a
 * new token from a response's X-CSRF-Token header, and this error has none:
 * CheckCsrfToken throws inside StartSession, before it adds the header (or
 * saves the new session). Since the go-live, 28 log-in attempts failed this
 * way, in runs ("400 400 400 400"), on top of failures for other reasons
 * (reported 2026-10-11: "de grosses difficultés pour me connecter").
 *
 * Safe to replay: CheckCsrfToken rejects the request before anything runs.
 * Only for methods CheckCsrfToken checks (not GET/HEAD/OPTIONS).
 */
export default function retryOnStaleCsrfToken() {
  override(app, 'request', function (original, options) {
    const method = (options.method || 'GET').toUpperCase();
    if (['GET', 'HEAD', 'OPTIONS'].includes(method)) return original(options);
    let stale = false;
    const errorHandler = options.errorHandler;
    return original({
      ...options,
      // Keeps the stale-token error quiet - it is about to be retried - and
      // hands every other one on unchanged (false = core's default handler,
      // what a request without its own errorHandler gets).
      errorHandler: error => {
        if (isStaleCsrfToken(error)) {
          stale = true;
          return;
        }
        return errorHandler ? errorHandler(error) : false;
      }
    }).catch(async error => {
      if (!stale) throw error;
      await refreshCsrfToken();

      // With its own errorHandler again: a second failure is reported as usual.
      return original(options);
    });
  });
}
function isStaleCsrfToken(error) {
  return error.status === 400 && !!error.response?.errors?.some(e => e.code === 'csrf_token_mismatch');
}

// Any response carries the current session's token in X-CSRF-Token (and the
// session cookie, a new session if the old one expired). A direct fetch, so
// as not to go through app.request - and this override - again.
async function refreshCsrfToken() {
  try {
    const response = await fetch(app.forum.attribute('apiUrl'), {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    const token = response.headers.get('X-CSRF-Token');
    if (token) app.session.csrfToken = token;
  } catch {
    // Offline or the like: the retry then fails and reports it as usual.
  }
}